import React from "react";
import PageBuilderSectionTitle from "./PageBuilderSectionTitle";
import { ComponentBackgroundColor } from "types/componentBackground";
import { WindowLocation } from "@reach/router";
import { graphql } from "gatsby";
import { PageBuilderOffersSelectionSliderFragment } from "types/graphql-types";
import OfferSliderCard from "components/Card/OfferSliderCard";
import ErrorBoundary from "components/ErrorBoundary";
import Slider, { SliderSlide } from "components/Slider";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { useRemoteOffers } from "src/utils/useRemoteOffers";

interface PageBuilderOffersSelectionSliderProps
  extends PageBuilderOffersSelectionSliderFragment {
  cardBackgroundColor?: ComponentBackgroundColor;
  location: WindowLocation;
}

interface OffersSelectionSliderProps {
  cardBackgroundColor?: ComponentBackgroundColor;
  location: WindowLocation;
  offers: PageBuilderOffersSelectionSliderFragment["offers"];
}

const OffersSelectionSlider: React.FC<OffersSelectionSliderProps> = ({
  cardBackgroundColor,
  location,
  offers
}) => {
  const language = useLanguage();
  const requiredOfferIds = offers?.map((o) => o?.sanityId) || [];

  const { data = [] } = useRemoteOffers(language, location);
  const filteredOffers = data.filter((offer) =>
    requiredOfferIds.includes(offer.sanityId)
  );

  return (
    <ErrorBoundary>
      <Slider
        variantColor={cardBackgroundColor}
        data-content-name="offers_slider"
      >
        {filteredOffers?.length > 0 &&
          filteredOffers.map((item) => {
            return (
              <SliderSlide key={item.sanityId}>
                <OfferSliderCard
                  offer={item}
                  cardBackgroundColor={cardBackgroundColor}
                />
              </SliderSlide>
            );
          })}
      </Slider>
    </ErrorBoundary>
  );
};

const PageBuilderOffersSelectionSlider: React.FC<PageBuilderOffersSelectionSliderProps> = ({
  heading,
  offers,
  cardBackgroundColor,
  location
}) => {
  return (
    <React.Fragment>
      <PageBuilderSectionTitle {...heading} />
      <OffersSelectionSlider
        offers={offers}
        cardBackgroundColor={cardBackgroundColor}
        location={location}
      />
    </React.Fragment>
  );
};

export default PageBuilderOffersSelectionSlider;

export const fragments = graphql`
  fragment PageBuilderOffersSelectionSlider on SanityOffersSelectionSlider {
    _key
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }
    offers {
      ... on SanityExclusiveOfferPerAirPromo {
        sanityId: _id
        _key
      }

      ... on SanityExclusiveOfferPerPromo {
        sanityId: _id
        _key
      }
    }
  }
`;
